import type { CheckoutErrorDetails } from '@iheartjane/types';
import { createExternalStore } from '@iheartjane/utils';
import { useSyncExternalStore } from 'react';

interface CheckoutStatus {
  checkoutError: string | null;
  checkoutErrorDetails: CheckoutErrorDetails | null;
  isCheckingOut: boolean;
}

const GENERIC_CHECKOUT_ERROR =
  'Something went wrong, please try again. If the problem persists, please contact us at support@iheartjane.com';

const INITIAL_STATUS: CheckoutStatus = {
  checkoutError: null,
  checkoutErrorDetails: null,
  isCheckingOut: false,
};

// TODO(carts-v3): derive from the checkout mutation once checkout moves to v3
const { notify, subscribe } = createExternalStore<CheckoutStatus>();

// In memory only: like the cart slice it replaces, it resets on reload.
let status = INITIAL_STATUS;

const update = (changes: Partial<CheckoutStatus>) => {
  status = { ...status, ...changes };
  notify();
};

export const getCheckoutStatus = () => status;

export const markCheckoutStarted = () => update({ isCheckingOut: true });

export const markCheckoutCancelled = () => update({ isCheckingOut: false });

export const markCheckoutSucceeded = () => update({ isCheckingOut: false });

/** `details` comes from `checkoutErrorDetails(error)`, which this package can't import. */
export const markCheckoutFailed = ({
  details,
  error,
}: {
  details?: CheckoutErrorDetails | null;
  error: unknown;
}) =>
  update({
    // CartAlerts renders a structured refusal; the banner would repeat it.
    checkoutError: details
      ? null
      : typeof error === 'string'
        ? error
        : GENERIC_CHECKOUT_ERROR,
    checkoutErrorDetails: details ?? null,
    isCheckingOut: false,
  });

export const clearCheckoutError = () =>
  update({ checkoutError: null, checkoutErrorDetails: null });

export const resetCheckoutStatus = () => {
  status = INITIAL_STATUS;
  notify();
};

export const useCheckoutStatus = () =>
  useSyncExternalStore(subscribe, getCheckoutStatus, () => INITIAL_STATUS);
